import React, { useState } from "react";
import MuiTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import { useTable, useSortBy, useFilters, useGlobalFilter } from "react-table";
import Typography from "@material-ui/core/Typography";
import { Alert, AlertTitle } from "@material-ui/lab";
import DashboardIcon from "@material-ui/icons/Dashboard";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  useTheme,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import { generalFilter, useDefaultColumn } from "../../../../util/filter";
import { useCustomerDetailsContext } from "../../../common/CustomerDetailsContext";
import { renderHeader } from "../../../common/util";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { getTheme } from "../../../common/getLocalTheme";
import { UseworkOrderImageUpload } from "../../../common/services/fileServices";
import { toast } from "react-toastify";
import { useFetchContext } from "../../../common/FetchContext";
import CloseIcon from "@material-ui/icons/Close";
import { useColumnMappingPost } from "../../projectManagementMutations";
import { useImportProjectData } from "../../../common/CommonMutations";
import { useAuthContext } from "../../../common/AuthContext";

export const ProjectsTable = ({ data = [] }: any) => {
  const theme = useTheme();
  const defaultColumn = useDefaultColumn();
  const { state, dispatch } = useCustomerDetailsContext();
  const { state: authState } = useAuthContext();

  const [open, setOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadResponse, setUploadResponse] = useState<any>(null);

  const { authAxios } = useFetchContext();
  const { mutateAsync: mutatePostColumn, isLoading } = useColumnMappingPost();

  const { mutateAsync: mutateAsset } = UseworkOrderImageUpload();
  const history = useHistory();
  const { customerId } = useParams() as {
    customerId: string;
  };

  const [selectedValues, setSelectedValues] = useState<Record<string, string>>(
    {},
  );

  const handleChange = (column: string, value: string) => {
    setSelectedValues((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  const handleClose = () => {
    setOpen(!open);
  };

  const handleConfirmSubmit = async () => {
    const unmappedColumns = staticColumns.filter(
      (column) => !selectedValues[column.key],
    );

    if (unmappedColumns.length > 0) {
      toast.error(
        `Please map the following columns: ${unmappedColumns
          .map((c) => c.label)
          .join(", ")}`,
      );
      return;
    }
    setConfirmOpen(false);
    try {
      await mutatePostColumn({
        authAxios,
        azurefile_id: uploadResponse?.azure_file_id,
        serial_number: selectedValues.serial_number,
        project_id: state.activeProjectId,
        bom: selectedValues.bom,
        isc: selectedValues.isc,
        voc: selectedValues.voc,
        imp: selectedValues.imp,
        vmp: selectedValues.vmp,
        pmp: selectedValues.pmp,
      });

      toast.success("Columns have been mapped successfully.");
      setConfirmOpen(false);
      handleClose();
    } catch (error) {
      toast.error("Error while mapping columns.");
    }

    console.log(selectedValues);
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
  };
  const staticColumns = [
    { key: "serial_number", label: "Serial Number" },
    { key: "bom", label: "Bom" },
    { key: "isc", label: "Isc" },
    { key: "voc", label: "Voc" },
    { key: "imp", label: "Imp" },
    { key: "vmp", label: "Vmp" },
    { key: "pmp", label: "Pmp" },
  ];
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  function handleFileUploadClick() {
    // console.log("Previous File:", selectedFile);

    fileInputRef.current?.click();
  }

  const { mutateAsync: mutateImportData, isLoading: isImporting } =
    useImportProjectData();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;
    // if (state.activeProjectId == null) {
    //   console.error("Please select a project.");
    //   return;
    // }

    const formData = new FormData();
    formData.append("project_id", String(state.activeProjectId));
    formData.append("file", file);

    try {
      const response = await mutateAsset({
        authAxios,
        formData,
      });

      setUploadResponse(response); // Save API response
      setSelectedValues({}); // Clear previous mappings
      setOpen(true);
      toast.success("File Uploaded Successfully..");

      console.log("Upload Success:", response);
    } catch (error) {
      console.error("Upload Failed:", error);
    }
  };
  const handleSubmitClick = () => {
    setConfirmOpen(true);
  };

  const handleImportData = async () => {
    try {
      const response = await mutateImportData({
        authAxios,
        project_id: Number(state.activeProjectId),
      });

      if (!response?.download_url) {
        toast.error("Download URL not found.");
        return;
      }

      // Download using authenticated request
      const fileResponse = await authAxios.get(response.download_url, {
        responseType: "blob",
      });

      // Create downloadable Blob
      const blob = new Blob([fileResponse.data], {
        type: "application/octet-stream",
      });

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      // Use filename directly from migration API response
      link.download = response.filename || "migration.pvel";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      toast.success("Project migrated and downloaded successfully.");
    } catch (error) {
      console.error("Migration/Download Failed:", error);
      toast.error("Failed to migrate or download project.");
    }
  };

  const openReportAssignment = (projectId: string) => {
    history.push(
      `/project_management/customer/${customerId}/projects/${projectId}/report_assignment`,
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Project Id",
        accessor: "id",
      },
      {
        Header: "Url",
        accessor: "url",
      },
      {
        id: "Number",
        accessor: "number",
        filter: generalFilter,
      },
      {
        id: "Project Manager",
        accessor: "project_manager_name",
        filter: generalFilter,
      },
      {
        id: "Start Date",
        accessor: "start_date",
        filter: generalFilter,
      },
      {
        id: "Disposition",
        accessor: "disposition_name",
        filter: generalFilter,
      },
      {
        id: "Report Assignment",
        Header: "Report Assignment",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }: any) => (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<DashboardIcon />}
            onClick={(e) => {
              e.stopPropagation();
              openReportAssignment(row.values.id);
            }}
            disabled={getTheme()}
          >
            View
          </Button>
        ),
      },
    ],
    [],
  );

  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    {
      data,
      columns,
      defaultColumn,
      initialState: {
        hiddenColumns: ["id", "url"],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
  );

  const openNewProjectForm = () => {
    history.push(`/project_management/customer/${customerId}/projects/add`);
  };
  const openEditProjectForm = (projectId: string | null) => () => {
    history.push(
      `/project_management/customer/${customerId}/projects/edit/${projectId}`,
    );
  };

  return (
    <>
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="center"
        spacing={2}
      >
        <Grid item xs={4}>
          <Typography variant="h5">Projects</Typography>
        </Grid>
        <Grid item xs={4}>
          <Box display="flex" style={{ gap: "10px" }} alignItems="center">
            <Button
              disabled={getTheme()}
              style={theme.btnNew}
              variant="contained"
              color="primary"
              onClick={openNewProjectForm}
            >
              New
            </Button>
            {state.activeProjectId &&
              (authState.user === "nagi@pvel.com" ||
                authState.user === "kevin.gibson@kiwa.com" ||
                authState.user === "jean.jaubert@kiwa.com") && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleImportData}
                  disabled={isImporting}
                >
                  Migrate this project
                </Button>
              )}
          </Box>
        </Grid>
        <Grid item xs={3}>
          <>
            {state.activeProjectId && (
              <>
                <Button
                  disabled={getTheme()}
                  // style={theme.btnWarning}
                  variant="contained"
                  color="secondary"
                  onClick={handleFileUploadClick}
                  style={{
                    minWidth: "100%",

                    whiteSpace: "normal",
                  }}
                >
                  Upload Manufacturer Characterisation
                </Button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv ,.xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </>
            )}
          </>
        </Grid>
        <Grid item xs={1}>
          {state.activeProjectId && (
            <Button
              disabled={getTheme()}
              style={theme.btnWarning}
              variant="contained"
              color="primary"
              onClick={openEditProjectForm(state.activeProjectId)}
            >
              Edit
            </Button>
          )}
        </Grid>
      </Grid>
      {!data.length && (
        <Alert severity="info">
          <AlertTitle>Info</AlertTitle>
          <strong>No Projects Available</strong>
        </Alert>
      )}
      {data.length > 0 && (
        <TableContainer data-testid="tableContainer" component={Paper}>
          <MuiTable {...getTableProps()} size="small">
            <TableHead>
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableCell style={theme.noLeftRightPadding}>
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={0}
                      >
                        <Grid item xs={12} {...column.getHeaderProps()}>
                          <Grid
                            container
                            direction="row"
                            alignItems="center"
                            spacing={0}
                          >
                            <Typography variant="subtitle2">
                              {column.id === "Report Assignment"
                                ? column.render("Header")
                                : renderHeader(column)}
                            </Typography>
                            {column.canFilter ? column.render("Filter") : null}
                            <Grid item xs={2}>
                              <IconButton
                                type="submit"
                                aria-label="search"
                                size="small"
                                {...column.getSortByToggleProps()}
                              >
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <ArrowDropDownIcon />
                                  ) : (
                                    <ArrowDropUpIcon />
                                  )
                                ) : (
                                  <CallSplitIcon />
                                )}
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <TableRow
                    {...row.getRowProps()}
                    selected={row.values.id === state.activeProjectId}
                    onClick={() => {
                      if (row.values.id === state.activeProjectId) {
                        dispatch({ type: "CLEAN_PROJECT" });
                      } else {
                        dispatch({
                          type: "PROJECT_SELECTED",
                          payload: {
                            projectId: row.values.id,
                            projectUrl: row.values.url,
                            project_name: row.values.number,
                          },
                        });
                      }
                    }}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <TableCell {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle id="id">
          <div>
            <Box display="flex" alignItems="center">
              <Box flexGrow={1}>{"Mapping Columns"}</Box>
              <Box>
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </div>
        </DialogTitle>
        <DialogContent>
          <Divider variant="fullWidth" />
          <Typography variant="h6" style={{ color: "red" }}>
            Ensure each column is mapped to the correct column from your file.
          </Typography>
          <TableContainer component={Paper} elevation={4}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: "40%", fontWeight: "bold" }}>
                    Pre Defined Measurement Column
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Uploaded Manufacturer Column
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staticColumns.map((column) => (
                  <TableRow key={column.key}>
                    <TableCell style={{ width: "40%", fontWeight: 600 }}>
                      {column.label}
                    </TableCell>

                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={selectedValues[column.key] || ""}
                          displayEmpty
                          onChange={(e) =>
                            handleChange(column.key, e.target.value as string)
                          }
                        >
                          <MenuItem value="">
                            <em>Select Column</em>
                          </MenuItem>

                          {uploadResponse?.columns.map((option: any) => {
                            const isSelected =
                              Object.values(selectedValues).includes(option);

                            return (
                              <MenuItem
                                key={option}
                                value={option}
                                disabled={
                                  isSelected &&
                                  selectedValues[column.key] !== option
                                }
                              >
                                {option}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default">
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitClick}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={handleCancelConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmation</DialogTitle>

        <DialogContent>
          Are you sure you want to submit the selected column mappings?
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelConfirm} color="default">
            No
          </Button>

          <Button
            onClick={handleConfirmSubmit}
            color="primary"
            variant="contained"
            disabled={isLoading}
            startIcon={
              isLoading ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {isLoading ? "Submitting..." : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isImporting}
        style={{
          zIndex: theme.zIndex.modal + 999,
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" align="center">
          Importing data...
          <br />
          This may take some time, please wait.
        </Typography>
      </Backdrop>
    </>
  );
};
